import React from "react";
import ReactECharts from "echarts-for-react";

function formatNumber(val) {
  const num = Number(val) || 0;
  return num.toFixed(2);
}

function formatWholeNumber(val) {
  const num = Number(val) || 0;
  return Math.round(num).toLocaleString();
}

function toMs(value) {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function irradiationValue(bucket) {
  const value = bucket?.irradiation_avg_wm2 ?? bucket?.gpoa_avg_wm2 ?? bucket?.irradiation_wm2;
  return value === null || typeof value === "undefined" ? null : Number(value) || 0;
}

export default function ProductionChart({ buckets, height = 320, sunrise, sunset }) {
  const data = Array.isArray(buckets) ? buckets : [];

  // Optional sunrise/sunset clamp to hide night-time noise on the chart.
  const sunriseMs = toMs(sunrise);
  const sunsetMs = toMs(sunset);
  const padMs = 30 * 60 * 1000; // 30 minutes in ms
  const daylightOnly =
    sunriseMs && sunsetMs
      ? data.filter((b) => {
          const hourMs = toMs(b.hour_start);
          const windowStart = sunriseMs - padMs;
          const windowEnd = sunsetMs + padMs;
          return hourMs && hourMs >= windowStart && hourMs <= windowEnd;
        })
      : data;

  // Still drop pure zero buckets so gaps collapse.
  const meaningful = daylightOnly.filter(
    (b) =>
      (Number(b.real_kwh) || 0) > 0 ||
      (Number(b.expected_kwh) || 0) > 0 ||
      (irradiationValue(b) || 0) > 0
  );
  const plotData = meaningful.length ? meaningful : daylightOnly;

  const categories = plotData.map((b) => b.hour_label || "");
  const realValues = plotData.map((b) => Number(b.real_kwh) || 0);
  const expectedValues = plotData.map((b) => Number(b.expected_kwh) || 0);
  const irradiationValues = plotData.map(irradiationValue);
  const maxVal = Math.max(...realValues, ...expectedValues, 0);
  const maxIrradiation = Math.max(...irradiationValues.map((value) => value || 0), 0);

  if (!data.length || (maxVal <= 0 && maxIrradiation <= 0)) {
    return <div className="text-sm text-slate-500">No hourly production data available for today.</div>;
  }

  const options = {
    color: ["#FFB74D", "#4FC3F7", "#0ea5e9"],
    grid: {
      top: 26,
      right: 64,
      bottom: 48,
      left: 76,
      borderColor: "#e2e8f0",
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#ffffff",
      borderColor: "#e2e8f0",
      textStyle: { color: "#1e293b", fontFamily: "Inter, system-ui, sans-serif" },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const title = items[0]?.axisValueLabel || items[0]?.name || "";
        const rows = items
          .map((item) => {
            const unit = item.seriesName === "Irradiation" ? "W/m²" : "kWh";
            const value = item.value === null || typeof item.value === "undefined" ? "—" : formatNumber(item.value);
            return `${item.marker} ${item.seriesName}: <strong>${value} ${unit}</strong>`;
          })
          .join("<br/>");
        return `<div><strong>${title}</strong><br/>${rows}</div>`;
      },
    },
    legend: {
      bottom: 0,
      textStyle: { color: "#1e293b", fontFamily: "Inter, system-ui, sans-serif" },
      itemWidth: 14,
      itemHeight: 10,
    },
    xAxis: {
      type: "category",
      data: categories,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        color: "#475569",
        fontSize: 11,
        rotate: -25,
      },
    },
    yAxis: [
      {
        type: "value",
        name: "Energy",
        min: 0,
        splitLine: { lineStyle: { color: "#e2e8f0", type: "dashed" } },
        axisLabel: {
          formatter: (val) => `${formatWholeNumber(val)} kWh`,
          color: "#475569",
          fontSize: 11,
        },
      },
      {
        type: "value",
        name: "Irradiation",
        min: 0,
        splitLine: { show: false },
        axisLabel: {
          formatter: (val) => `${Number(val).toFixed(0)} W/m²`,
          color: "#64748b",
          fontSize: 11,
        },
      },
    ],
    series: [
      {
        name: "Real",
        type: "bar",
        yAxisIndex: 0,
        data: realValues,
        barMaxWidth: 24,
        itemStyle: { borderRadius: [6, 6, 0, 0], opacity: 0.95 },
      },
      {
        name: "Theoretical",
        type: "bar",
        yAxisIndex: 0,
        data: expectedValues,
        barMaxWidth: 24,
        itemStyle: { borderRadius: [6, 6, 0, 0], opacity: 0.85 },
      },
      {
        name: "Irradiation",
        type: "line",
        yAxisIndex: 1,
        data: irradiationValues,
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        connectNulls: true,
        lineStyle: { width: 2 },
      },
    ],
  };

  return <ReactECharts option={options} style={{ height, width: "100%" }} notMerge lazyUpdate />;
}
